import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { AUDIT_LOG_SELECT, AuditActor } from "@/utils/auditActor"

export const SHIFT_AUDIT_LOGS_PAGE_SIZE = 50

export interface AuditLogChange {
  before: unknown
  after: unknown
}

export interface ShiftAuditLog extends AuditActor {
  id: string
  created_at: string
  table_name: string
  record_id: string
  action: "insert" | "update" | "delete"
  comment: string | null
  changes: Record<string, AuditLogChange>
}

interface UseShiftAuditLogsOptions {
  shiftId?: string
  timesheetId?: string
  enabled?: boolean
  limit?: number
}

interface ShiftAuditLogsResult {
  entries: ShiftAuditLog[]
  hasMore: boolean
}

const fetchAuditLogsForRecordIds = async ({
  tableName,
  recordIds,
  limit,
}: {
  tableName: "shifts" | "timesheets"
  recordIds: string[]
  limit: number
}) => {
  if (recordIds.length === 0) {
    return []
  }

  let query = supabase.from("audit_logs").select(AUDIT_LOG_SELECT).eq("table_name", tableName)

  query =
    recordIds.length === 1 ? query.eq("record_id", recordIds[0]) : query.in("record_id", recordIds)

  const { data, error } = await query.order("created_at", { ascending: false }).limit(limit)

  if (error) {
    throw new Error(error.message)
  }

  return (data as unknown as ShiftAuditLog[] | null) ?? []
}

export const useShiftAuditLogs = ({
  shiftId,
  timesheetId,
  enabled = true,
  limit = SHIFT_AUDIT_LOGS_PAGE_SIZE,
}: UseShiftAuditLogsOptions) => {
  return useQuery<ShiftAuditLogsResult>({
    queryKey: ["shift-audit-logs", shiftId, timesheetId, limit],
    enabled: Boolean(shiftId) && enabled,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      if (!shiftId) {
        return { entries: [], hasMore: false }
      }

      const fetchLimit = limit + 1

      const relatedTimesheetIds = new Set<string>()

      if (timesheetId) {
        relatedTimesheetIds.add(timesheetId)
      }

      const { data: relatedTimesheets, error: relatedTimesheetsError } = await supabase
        .from("timesheets")
        .select("id")
        .eq("shift_id", shiftId)

      if (relatedTimesheetsError) {
        throw new Error(relatedTimesheetsError.message)
      }

      ;(relatedTimesheets ?? []).forEach((timesheet) => {
        if (timesheet?.id) {
          relatedTimesheetIds.add(timesheet.id)
        }
      })

      const [shiftLogs, timesheetLogs] = await Promise.all([
        fetchAuditLogsForRecordIds({
          tableName: "shifts",
          recordIds: [shiftId],
          limit: fetchLimit,
        }),
        fetchAuditLogsForRecordIds({
          tableName: "timesheets",
          recordIds: [...relatedTimesheetIds],
          limit: fetchLimit,
        }),
      ])

      const entries = [...shiftLogs, ...timesheetLogs].sort(
        (left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime(),
      )

      return {
        entries: entries.slice(0, limit),
        hasMore: entries.length > limit,
      }
    },
  })
}
